/**
 * Bub and Bob, the bubble dragons (05 §6.1), modelled on the official art (the owner's
 * reference: the Bubble Bobble 4 Friends cover, 2026-09-23): little round dinosaurs with a big
 * head, tall oval eyes set close together, a broad muzzle with an open smile and two small
 * teeth, round cheek patches, rounded spines down the back, a big white belly, magenta hands
 * and feet with white claws, and a big tapering tail. Bub is green with yellow spines and cheeks,
 * Bob blue with pale blue ones.
 *
 * Built in tiles (one tile = 8 pixels of the original): about two tiles tall, like the 16x16
 * sprite. Local frame: feet at the origin, facing +x, +z toward the camera.
 */

import { type Color, type Material, Matrix4, Vector3 } from 'three';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { Palette } from '../palette';
import { Puppet } from './puppet';

export type DragonColour = 'bub' | 'bob';

/** A rounded spine, as on the box art: an egg standing out along +y, turned by `roll` about z. */
function spine(mb: MeshBuilder, x: number, y: number, roll: number, size: number, p: Palette, trim: string): void {
  const m = new Matrix4().makeTranslation(x, y, 0).multiply(new Matrix4().makeRotationZ(roll));
  mb.blob(size * 0.36, size * 0.55, size * 0.3, p.color(`${trim}.1`), m.multiply(place(0, size * 0.2, 0)), {
    topColor: p.color(`${trim}.2`), detail: 2,
  });
}

/**
 * A smooth tapered tube through `points` with a radius per point: the tail. Rings of `sides`
 * vertices, smooth normals, the tip closed to a point.
 */
function tube(mb: MeshBuilder, points: readonly Vector3[], radii: readonly number[], color: Color, sides = 12): void {
  const rings: Array<{ p: Vector3; n: Vector3 }[]> = [];
  points.forEach((pt, i) => {
    const next = points[Math.min(points.length - 1, i + 1)] ?? pt;
    const prev = points[Math.max(0, i - 1)] ?? pt;
    const along = next.clone().sub(prev).normalize();
    const side = new Vector3(0, 1, 0).cross(along).normalize();
    const up = along.clone().cross(side).normalize();
    const r = radii[i] ?? 0;
    const ring: { p: Vector3; n: Vector3 }[] = [];
    for (let k = 0; k < sides; k++) {
      const a = (k / sides) * Math.PI * 2;
      const n = side.clone().multiplyScalar(Math.cos(a)).addScaledVector(up, Math.sin(a));
      ring.push({ p: pt.clone().addScaledVector(n, r), n });
    }
    rings.push(ring);
  });
  for (let i = 0; i + 1 < rings.length; i++) {
    const a = rings[i] ?? [];
    const b = rings[i + 1] ?? [];
    for (let k = 0; k < sides; k++) {
      const a0 = a[k];
      const a1 = a[(k + 1) % sides];
      const b0 = b[k];
      const b1 = b[(k + 1) % sides];
      if (!a0 || !a1 || !b0 || !b1) continue;
      mb.triSmooth(a0.p, b1.p, b0.p, a0.n, b1.n, b0.n, color);
      mb.triSmooth(a0.p, a1.p, b1.p, a0.n, a1.n, b1.n, color);
    }
  }
}

/** Three white claws along the front of a hand or foot. */
function claws(mb: MeshBuilder, p: Palette, x: number, y: number, spread: number, size: number): void {
  for (const dz of [-spread, 0, spread]) mb.blob(size, size * 0.8, size, p.color('paper.0'), place(x, y, dz), { detail: 0 });
}

function body(p: Palette, skin: string, trim: string): MeshBuilder {
  const mb = new MeshBuilder();
  // A chubby, pear-round body with a big white belly on the front, turned a little toward the
  // camera side (+z), where the light reaches it.
  mb.blob(0.5, 0.48, 0.48, p.color(`${skin}.2`), place(-0.04, 0.5, 0), { detail: 3 });
  mb.blob(0.34, 0.42, 0.4, p.color('paper.0'), place(0.24, 0.44, 0.14), { detail: 3 });
  // A big tail sweeping out behind and toward the camera side, so it shows past the body,
  // tapering to a tip that curls up; spines along its top.
  const tailDir = { x: -0.62, z: 0.78 };
  const tailAt = (t: number): { d: number; y: number; r: number } => ({
    d: 0.2 + t * 1.12,
    y: 0.3 - t * 0.14 + t * t * 0.34,
    r: 0.3 * (1 - t * 0.8),
  });
  const along: Vector3[] = [];
  const radii: number[] = [];
  for (let i = 0; i <= 10; i++) {
    const { d, y, r } = tailAt(i / 10);
    along.push(new Vector3(tailDir.x * d, y, tailDir.z * d));
    radii.push(i === 10 ? 0.005 : r);
  }
  tube(mb, along, radii, p.color(`${skin}.2`));
  // Spines standing on the tail's top, leaning back along it.
  for (const [t, size] of [[0.36, 0.24], [0.66, 0.18]] as const) {
    const { d, y, r } = tailAt(t);
    const m = new Matrix4().makeTranslation(tailDir.x * d, 0, tailDir.z * d)
      .multiply(new Matrix4().makeRotationY(Math.atan2(tailDir.z, -tailDir.x)));
    mb.with(m, () => spine(mb, 0, y + r * 0.7, 0.5, size, p, trim));
  }
  // Spines down the back, continuing those of the head.
  spine(mb, -0.42, 0.88, 1.0, 0.3, p, trim);
  spine(mb, -0.54, 0.6, 1.35, 0.27, p, trim);
  return mb;
}

function head(p: Palette, skin: string, trim: string): MeshBuilder {
  const mb = new MeshBuilder();
  // The head is the biggest thing about him, as on the box art.
  mb.blob(0.64, 0.56, 0.6, p.color(`${skin}.2`), place(0.02, 0.42, 0), { detail: 3 });
  // The broad muzzle, a wide open smile with a pink tongue, and two small teeth at the top.
  mb.blob(0.38, 0.28, 0.52, p.color(`${skin}.2`), place(0.46, 0.16, 0), { detail: 3 });
  // The smile: a dark opening set into the underside of the muzzle, the tongue inside it.
  mb.blob(0.14, 0.11, 0.34, p.color('mouth.0'), place(0.66, 0.0, 0), { detail: 2 });
  mb.blob(0.09, 0.06, 0.2, p.color('mouth.1'), place(0.72, -0.04, 0), { detail: 1 });
  mb.box(0.07, 0.08, 0.07, p.color('paper.0'), place(0.78, 0.02, 0.09));
  mb.box(0.07, 0.08, 0.07, p.color('paper.0'), place(0.78, 0.02, -0.09));
  // Round cheek patches on either side of the muzzle.
  mb.blob(0.16, 0.14, 0.07, p.color(`${trim}.1`), place(0.36, 0.16, 0.52), { detail: 2 });
  mb.blob(0.16, 0.14, 0.07, p.color(`${trim}.1`), place(0.36, 0.16, -0.52), { detail: 2 });
  // Rounded spines over the crown and down the back of the head.
  spine(mb, -0.1, 0.96, 0.25, 0.34, p, trim);
  spine(mb, -0.42, 0.82, 0.7, 0.34, p, trim);
  spine(mb, -0.58, 0.5, 1.15, 0.32, p, trim);
  return mb;
}

/** A tall oval eye, white, with a tall black pupil looking ahead. */
function eye(p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  mb.blob(0.2, 0.34, 0.17, p.color('eye.1'), place(0, 0, 0), { detail: 3 });
  mb.blob(0.07, 0.15, 0.08, p.color('eye.0'), place(0.16, -0.04, 0.02), { detail: 2 });
  return mb;
}

function hand(p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  mb.blob(0.16, 0.13, 0.14, p.color('limb.1'), place(0.04, 0, 0), { topColor: p.color('limb.2'), detail: 2 });
  claws(mb, p, 0.18, 0.02, 0.07, 0.04);
  return mb;
}

function foot(p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  mb.blob(0.28, 0.14, 0.2, p.color('limb.1'), place(0.08, 0, 0), { topColor: p.color('limb.2'), flatBottom: true, detail: 2 });
  claws(mb, p, 0.34, 0.02, 0.09, 0.05);
  return mb;
}

/** The mouth wide open, for blowing: shown in place of the smile. */
function blowMouth(p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  mb.blob(0.14, 0.16, 0.24, p.color('mouth.0'), place(0, 0, 0), { detail: 1 });
  mb.blob(0.08, 0.06, 0.16, p.color('mouth.1'), place(0.04, -0.08, 0), { detail: 0 });
  return mb;
}

/** Part names every dragon pose may move. */
export const DRAGON_PARTS = [
  'body', 'head', 'eyeNear', 'eyeFar', 'footNear', 'footFar', 'armNear', 'armFar', 'mouth',
] as const;

export function buildDragon(p: Palette, skin: DragonColour, material: Material): Puppet {
  const trim = skin === 'bub' ? 'bubTrim' : 'bobTrim';
  const pup = new Puppet(material);
  pup.turn = 0.85;
  pup.add('footNear', foot(p), 0.02, 0.12, 0.26);
  pup.add('footFar', foot(p), 0.02, 0.12, -0.26);
  pup.add('body', body(p, skin, trim), 0, 0.04, 0);
  pup.add('head', head(p, skin, trim), 0.06, 0.9, 0, 'body');
  pup.add('eyeNear', eye(p), 0.46, 0.66, 0.17, 'head');
  pup.add('eyeFar', eye(p), 0.46, 0.66, -0.17, 'head');
  pup.add('armNear', hand(p), 0.34, 0.66, 0.36, 'body');
  pup.add('armFar', hand(p), 0.34, 0.66, -0.36, 'body');
  const m = pup.add('mouth', blowMouth(p), 0.76, 0.06, 0, 'head');
  m.visible = false;
  return pup;
}
